import api from "@/config/api";
import { EmailNotifications, Organization } from "@/types/org";
import { i18n } from "@/utils/i18n";
import { log } from "@/utils/logger";
import { isEmail } from "@/utils/validations";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";

const RECIPIENTS_MAX = 10;

// oid is the organization in the URL, not the one in client storage: storage is
// shared between tabs, so another tab switching organization would otherwise
// redirect this form's save to an organization the user is not looking at.
const useNotificationSettings = (oid: string | undefined, settings?: EmailNotifications) => {
    const queryClient = useQueryClient();

    const [enabled, setEnabled] = React.useState(false);
    const [recipients, setRecipients] = React.useState<string[]>([]);
    const [error, setError] = React.useState("");
    const [saving, setSaving] = React.useState(false);

    // Serialised rather than compared by reference: the organization query
    // refetches on window focus, and re-seeding on a new object identity would
    // discard recipients the user had typed but not yet saved.
    const saved = JSON.stringify({
        enabled: settings?.enabled ?? false,
        recipients: settings?.recipients ?? [],
    });

    React.useEffect(() => {
        const value = JSON.parse(saved) as EmailNotifications;

        setEnabled(value.enabled);
        setRecipients(value.recipients);
        setError("");
    }, [saved]);

    const isDirty = saved !== JSON.stringify({ enabled, recipients });

    const onChangeRecipients = (next: string[]) => {
        const normalized: string[] = [];

        for (const recipient of next) {
            const email = recipient.trim().toLowerCase();

            if (!email) continue;

            if (!isEmail(email)) {
                return setError(i18n.t("validation.notifications.email.invalid"));
            }

            if (normalized.includes(email)) {
                return setError(i18n.t("validation.notifications.email.duplicate"));
            }

            normalized.push(email);
        }

        if (normalized.length > RECIPIENTS_MAX) {
            return setError(
                i18n.t("validation.notifications.recipients.max", { max: RECIPIENTS_MAX }),
            );
        }

        setError("");
        setRecipients(normalized);
    };

    const onChangeEnabled = (checked: boolean) => {
        setError("");
        setEnabled(checked);
    };

    const onSave = async () => {
        if (!oid) return;

        if (enabled && recipients.length === 0) {
            return setError(i18n.t("validation.notifications.recipients.required"));
        }

        try {
            setSaving(true);

            const data = await api.patch<EmailNotifications>(
                `/organizations/${oid}/notifications`,
                { data: { enabled, recipients } },
            );

            queryClient.setQueryData<Organization>(["organization", oid], previous =>
                previous ? { ...previous, emailNotifications: data } : previous,
            );

            log.success(i18n.t("notificationsUpdate", { scope: "messages.success" }));
        } catch (e) {
            log.error(e, "onSaveNotificationSettings");
        } finally {
            setSaving(false);
        }
    };

    return {
        enabled,
        recipients,
        error,
        saving,
        isDirty,
        onChangeEnabled,
        onChangeRecipients,
        onSave,
    };
};

export { useNotificationSettings, RECIPIENTS_MAX };
